import type { DesktopCallAction, DesktopDownloads, DesktopShortcuts, SessionInfo } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Laptop, Monitor, Smartphone, Volume2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { apiUrl, client } from '../lib/client';
import { desktop } from '../lib/desktop';
import { errorMessage } from '../lib/errors';
import { createDenoiser, denoiseContext } from '../lib/denoise';
import { sounds } from '../lib/sounds';
import { canNotify, notificationsOn, setNotifications, testNotification } from '../lib/notify';
import { formatShortcut, readShortcut } from '../lib/shortcuts';
import { useDevices } from '../state/devices';
import { closeOnBackdrop } from '../lib/dismiss';
import { JiraSettings, useJira } from './JiraSettings';

/** Opened from the user menu or the call controls; rendered once by Shell. */
type SettingsTab = 'media' | 'shortcuts' | 'sessions' | 'connections';
export const useSettings = create<{ open: boolean; tab: SettingsTab; show(tab?: SettingsTab): void; hide(): void }>()((set) => ({
  open: false,
  tab: 'media',
  show: (tab = 'media') => set({ open: true, tab }),
  hide: () => set({ open: false }),
}));

export const canPickSpeaker = 'setSinkId' in HTMLMediaElement.prototype;

export function SettingsDialog() {
  const open = useSettings((s) => s.open);
  return open ? <Dialog /> : null;
}

function Dialog() {
  const dialog = useRef<HTMLDialogElement>(null);
  const [tab, setTab] = useState<SettingsTab>(() => useSettings.getState().tab);
  const jira = useJira();
  useEffect(() => dialog.current?.showModal(), []);
  const tabClass = (active: boolean) =>
    `h-9 rounded-sm border-0 px-3 text-sm font-semibold ${active ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-2 hover:text-fg-1'}`;

  return (
    <dialog onClick={closeOnBackdrop}
      ref={dialog}
      onClose={() => useSettings.getState().hide()}
      aria-labelledby="settings-title"
      className="m-auto w-[min(520px,calc(100vw-24px))] rounded-xl border border-line-strong bg-surface-1 p-0 text-fg-1 backdrop:bg-black/60"
    >
      <div className="flex items-center justify-between px-6 pt-5">
        <h2 id="settings-title" className="m-0 font-heading text-xl font-bold">
          Ayarlar
        </h2>
        <button type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className="grid size-8 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      <div role="tablist" className="mx-6 mt-3 flex flex-wrap gap-1 border-b border-line pb-2">
        <button type="button" role="tab" aria-selected={tab === 'media'} onClick={() => setTab('media')} className={tabClass(tab === 'media')}>
          Genel
        </button>
        {desktop?.setShortcut && (
          <button type="button" role="tab" aria-selected={tab === 'shortcuts'} onClick={() => setTab('shortcuts')} className={tabClass(tab === 'shortcuts')}>
            Kısayollar
          </button>
        )}
        <button type="button" role="tab" aria-selected={tab === 'sessions'} onClick={() => setTab('sessions')} className={tabClass(tab === 'sessions')}>
          Oturumlar
        </button>
        {jira.data?.available && (
          <button type="button" role="tab" aria-selected={tab === 'connections'} onClick={() => setTab('connections')} className={tabClass(tab === 'connections')}>
            Bağlantılar
          </button>
        )}
      </div>
      <div className="max-h-[70dvh] overflow-y-auto px-6 pt-4 pb-6">
        {tab === 'media' ? <MediaSettings /> : tab === 'shortcuts' ? <ShortcutSettings /> : tab === 'connections' ? <JiraSettings /> : <Sessions />}
      </div>
    </dialog>
  );
}

export function useDeviceList() {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const refresh = useCallback(async () => setDevices(await navigator.mediaDevices.enumerateDevices()), []);
  useEffect(() => {
    void refresh();
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refresh);
  }, [refresh]);
  return { devices, refresh };
}

function MediaSettings() {
  const prefs = useDevices();
  const { devices, refresh } = useDeviceList();
  const [testing, setTesting] = useState(false);
  const [listen, setListen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Browsers hide device names until a permission was granted once.
  const unnamed = devices.length > 0 && devices.every((d) => !d.label);

  async function allow() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      await refresh();
    } catch {
      setNotice('Mikrofon izni verilmedi. Tarayıcının adres çubuğundaki kilit simgesinden izin verebilirsin.');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <NotificationSetting />
      <LaunchAtLoginSetting />
      <DesktopAppDownloads />
      {unnamed && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line-strong bg-surface-2 p-3 text-[13.5px] text-fg-2">
          <span className="min-w-0 flex-1">Cihaz adlarını görmek için mikrofon izni gerekiyor.</span>
          <button type="button" onClick={() => void allow()} className="nc-press h-9 rounded-sm border-0 bg-brand-500 px-3 text-sm font-bold text-white">
            İzin ver
          </button>
        </div>
      )}
      {notice && <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">{notice}</p>}

      <DeviceSelect label="Mikrofon" kind="audioinput" devices={devices} value={prefs.micId} onChange={(micId) => prefs.set({ micId })} />
      <div className="flex flex-col gap-2">
        <MicLevel deviceId={prefs.micId} active={testing} listen={listen} />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setTesting((t) => !t)}
            className="nc-press h-9 rounded-sm border border-line-strong bg-surface-2 px-3 text-sm font-semibold"
          >
            {testing ? 'Testi bitir' : 'Mikrofonu test et'}
          </button>
          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-fg-2">
            <input type="checkbox" checked={listen} onChange={(e) => setListen(e.target.checked)} className="size-4 accent-brand-500" />
            Kendini dinle (kulaklıkla)
          </label>
        </div>
        <p className="m-0 text-[12.5px] text-fg-3">Test, seçili gürültü engellemeden geçmiş sesi gösterir; ayarları değiştirerek karşılaştırabilirsin.</p>
      </div>
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1.5 text-[13px] font-semibold text-fg-2">Gürültü engelleme</legend>
        {(
          [
            ['strong', 'Güçlü (önerilen)', 'Yapay zekâ modeli (RNNoise). Klavye, fan ve ortam sesini keser.'],
            ['experimental', 'Deneysel', 'Daha yeni bir model (GTCRN). Odanın yankısında daha iyi olabilir.'],
            ['browser', 'Tarayıcının kendi', 'Hafif; sadece sabit uğultuyu azaltır.'],
            ['off', 'Kapalı', 'Müzik ya da enstrüman paylaşırken.'],
          ] as const
        ).map(([value, title, hint]) => (
          <label key={value} className="flex cursor-pointer items-start gap-3 text-sm">
            <input type="radio" name="denoise" checked={prefs.denoise === value} onChange={() => prefs.set({ denoise: value })} className="mt-0.5 size-4 accent-brand-500" />
            <span>
              <span className="block font-semibold">{title}</span>
              <span className="block text-[13px] text-fg-3">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <label className="flex cursor-pointer items-start gap-3 text-sm">
        <input type="checkbox" checked={prefs.autoGain} onChange={(e) => prefs.set({ autoGain: e.target.checked })} className="mt-0.5 size-4 accent-brand-500" />
        <span>
          <span className="block font-semibold">Otomatik ses seviyesi</span>
          <span className="block text-[13px] text-fg-3">Kısık sesi yükseltir. Sesin yankılı ya da boş odada gibi geliyorsa kapatmayı dene.</span>
        </span>
      </label>

      <label className="flex cursor-pointer items-start gap-3 text-sm">
        <input type="checkbox" checked={prefs.othersMicCue} onChange={(e) => prefs.set({ othersMicCue: e.target.checked })} className="mt-0.5 size-4 accent-brand-500" />
        <span>
          <span className="block font-semibold">Biri mikrofonunu açınca ses çal</span>
          <span className="block text-[13px] text-fg-3">Odada biri konuşmaya hazırlanınca kısa ve kısık bir ses duyarsın.</span>
        </span>
      </label>
      {canPickSpeaker && (
        <div className="flex flex-col gap-2">
          <DeviceSelect label="Hoparlör" kind="audiooutput" devices={devices} value={prefs.speakerId} onChange={(speakerId) => prefs.set({ speakerId })} />
          <button type="button" onClick={sounds.test} className="nc-press flex h-9 items-center gap-2 self-start rounded-sm border border-line-strong bg-surface-2 px-3 text-sm font-semibold">
            <Volume2 size={15} />
            Ses çal
          </button>
        </div>
      )}
      <DeviceSelect label="Kamera" kind="videoinput" devices={devices} value={prefs.cameraId} onChange={(cameraId) => prefs.set({ cameraId })} />
    </div>
  );
}

const onPhone = /iPhone|iPad|Android/.test(navigator.userAgent);
const onMac = /Mac/.test(navigator.platform);

/** In the browser on a computer: where to get the desktop app (only what has been released). */
function DesktopAppDownloads() {
  const { data } = useQuery({
    queryKey: ['downloads'],
    queryFn: async (): Promise<DesktopDownloads> => (await fetch(`${apiUrl}/downloads`)).json(),
    enabled: !desktop && !onPhone,
    staleTime: 10 * 60_000,
  });
  if (desktop || onPhone || !data || (!data.mac && !data.windows)) return null;
  const mac = data.mac
    ? [
        data.mac.arm64 && { label: 'macOS · Apple Silicon (M1 ve sonrası)', file: data.mac.arm64 },
        data.mac.x64 && { label: 'macOS · Intel', file: data.mac.x64 },
      ].filter((link): link is { label: string; file: string } => !!link)
    : [];
  const windows = data.windows ? [{ label: 'Windows', file: data.windows.x64 }] : [];
  // The computer's own system first.
  const links = onMac ? [...mac, ...windows] : [...windows, ...mac];
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm">
        <span className="block font-semibold">Masaüstü uygulaması</span>
        <span className="block text-[13px] text-fg-3">
          Arka planda da çalışan kısayollar, ekran paylaşımı için pencere seçici ve her zaman üstte duran görüşme penceresi. Kendini günceller.
        </span>
      </span>
      <div className="flex flex-wrap gap-2">
        {links.map((link) => (
          <a
            key={link.file}
            href={`${apiUrl}/updates/${link.file}`}
            download
            className="nc-press flex h-9 items-center gap-2 rounded-sm border border-line-strong bg-surface-2 px-3 text-sm font-semibold text-fg-1 no-underline"
          >
            <Download size={15} aria-hidden="true" />
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
}

/** Desktop app only: open NovaChat when the user signs in to the computer. */
function LaunchAtLoginSetting() {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    void desktop?.getLaunchAtLogin?.().then(setOn);
  }, []);
  const set = desktop?.setLaunchAtLogin;
  if (!set) return null;
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm">
      <input
        type="checkbox"
        checked={on ?? false}
        disabled={on === null}
        onChange={async (e) => setOn(await set(e.target.checked))}
        className="mt-0.5 size-4 accent-brand-500"
      />
      <span>
        <span className="block font-semibold">Bilgisayar açılınca başlat</span>
        <span className="block text-[13px] text-fg-3">Bilgisayarda oturum açınca NovaChat kendiliğinden açılır.</span>
      </span>
    </label>
  );
}

const SHORTCUTS: readonly [DesktopCallAction, string, string][] = [
  ['mic', 'Mikrofonu aç/kapat', ''],
  ['deafen', 'Sesi aç/kapat', 'Kimseyi duymazsın, mikrofonun da kapanır.'],
];

/** Desktop app only: the call's global shortcuts, recorded by pressing the keys. */
function ShortcutSettings() {
  const [keys, setKeys] = useState<DesktopShortcuts | null>(null);
  const [recording, setRecording] = useState<DesktopCallAction | null>(null);
  const [message, setMessage] = useState<{ action: DesktopCallAction; text: string } | null>(null);

  useEffect(() => {
    void desktop?.getShortcuts?.().then(setKeys);
  }, []);

  async function save(action: DesktopCallAction, accelerator: string | null) {
    const result = await desktop?.setShortcut?.(action, accelerator);
    if (result === 'ok') {
      setMessage(null);
      setKeys((await desktop?.getShortcuts?.()) ?? null);
    } else {
      setMessage({ action, text: result === 'taken' ? 'Bu tuşları başka bir uygulama kullanıyor. Başka bir kısayol dene.' : 'Bu kısayol kullanılamaz.' });
    }
  }

  useEffect(() => {
    if (!recording) return;
    // The current shortcuts let go, otherwise pressing them would reach the system instead of this page.
    desktop?.pauseShortcuts?.(true);
    const onKey = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') return setRecording(null);
      const read = readShortcut(event);
      if (!read) return;
      if ('problem' in read) return setMessage({ action: recording, text: read.problem });
      if (read.accelerator === keys?.[recording === 'mic' ? 'deafen' : 'mic']) return setMessage({ action: recording, text: 'Bu tuşlar diğer kısayolda kullanılıyor.' });
      setRecording(null);
      void save(recording, read.accelerator);
    };
    const cancel = () => setRecording(null);
    // Capture: the keys must not reach the dialog (Esc would close it) or the app's own shortcuts.
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', cancel);
      desktop?.pauseShortcuts?.(false);
    };
  }, [recording, keys]);

  return (
    <div className="flex flex-col gap-5">
      <p className="m-0 text-[13.5px] text-fg-2">
        Görüşmedeyken bu tuşlar NovaChat arka plandayken de çalışır. Görüşme dışında tuşlar diğer uygulamalarda kalır.
      </p>
      {SHORTCUTS.map(([action, label, hint]) => {
        const current = keys?.[action] ?? null;
        const listening = recording === action;
        return (
          <div key={action} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <span id={`shortcut-${action}`} className="min-w-0 flex-1 text-sm font-semibold">
                {label}
              </span>
              <button
                type="button"
                id={`shortcut-${action}-keys`}
                // Read as "Mikrofonu aç/kapat ⇧⌘M", or "… Tuşlara bas…" while recording.
                aria-labelledby={`shortcut-${action} shortcut-${action}-keys`}
                title={listening ? 'Vazgeçmek için Esc' : 'Değiştirmek için tıkla ve yeni tuşlara bas'}
                disabled={!keys}
                aria-pressed={listening}
                onClick={() => {
                  setMessage(null);
                  setRecording(listening ? null : action);
                }}
                onBlur={() => listening && setRecording(null)}
                className={`nc-press h-9 min-w-28 shrink-0 rounded-sm border px-3 text-sm font-semibold ${listening ? 'border-brand-500 bg-surface-3 text-fg-1' : 'border-line-strong bg-surface-2'}`}
              >
                {listening ? 'Tuşlara bas…' : current ? <kbd className="font-mono">{formatShortcut(current)}</kbd> : 'Yok'}
              </button>
              {current && !listening ? (
                <button
                  type="button"
                  aria-label={`Kısayolu kaldır: ${label}`}
                  onClick={() => void save(action, null)}
                  className="grid size-9 shrink-0 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2"
                >
                  <X size={16} />
                </button>
              ) : (
                // Keeps the key buttons of both rows in line.
                <span className="size-9 shrink-0" />
              )}
            </div>
            {hint && <p className="m-0 text-[13px] text-fg-3">{hint}</p>}
            {message?.action === action && (
              <p role="alert" className="m-0 text-[12.5px] text-[#EE9191]">
                {message.text}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NotificationSetting() {
  const [on, setOn] = useState(notificationsOn);
  const [blocked, setBlocked] = useState(false);
  const [tested, setTested] = useState<'shown' | 'failed' | null>(null);
  if (!canNotify) return null;
  return (
    <div className="flex flex-col gap-2">
    <label className="flex cursor-pointer items-start gap-3 text-sm">
      <input
        type="checkbox"
        checked={on}
        onChange={async (e) => {
          const next = await setNotifications(e.target.checked);
          setOn(next);
          setBlocked(e.target.checked && !next);
        }}
        className="mt-0.5 size-4 accent-brand-500"
      />
      <span>
        <span className="block font-semibold">Masaüstü bildirimleri</span>
        <span className="block text-[13px] text-fg-3">
          {blocked
            ? 'Tarayıcı bildirimleri engelliyor. Adres çubuğundaki kilit simgesinden izin verebilirsin.'
            : 'DM’ler ve seni anan mesajlar için; baktığın kanal ve “Rahatsız etmeyin” hariç.'}
        </span>
      </span>
    </label>
      {on && (
        <div className="flex flex-col gap-1.5 pl-7">
          <button
            type="button"
            onClick={() => setTested(testNotification() ? 'shown' : 'failed')}
            className="nc-press h-9 self-start rounded-sm border border-line-strong bg-surface-2 px-3 text-sm font-semibold"
          >
            Deneme bildirimi gönder
          </button>
          {tested && (
            <p className="m-0 text-[12.5px] text-fg-3">
              {tested === 'failed'
                ? 'Bu tarayıcı sayfa bildirimi gösteremiyor (telefonlarda Web Push gerekiyor).'
                : `Bildirim görünmediyse işletim sistemi engelliyor: macOS’ta Sistem Ayarları › Bildirimler › ${desktop ? 'NovaChat' : 'tarayıcın'} için izin ver ve Odaklanma/Rahatsız Etme kapalı olsun. Windows’ta Ayarlar › Sistem › Bildirimler.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DeviceSelect({
  label,
  kind,
  devices,
  value,
  onChange,
}: {
  label: string;
  kind: MediaDeviceKind;
  devices: MediaDeviceInfo[];
  value: string;
  onChange: (id: string) => void;
}) {
  const options = devices.filter((d) => d.kind === kind && d.deviceId && d.deviceId !== 'default');
  // A device unplugged since it was chosen falls back to the system default.
  const current = options.some((d) => d.deviceId === value) ? value : 'default';
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold text-fg-2">{label}</span>
      <select
        value={current}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 focus:border-brand-400 focus:outline-none sm:text-sm"
      >
        <option value="default">Sistem varsayılanı</option>
        {options.map((d, i) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label || `${label} ${i + 1}`}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Live level of the mic after the chosen noise suppression, only while testing (opening settings
 * never turns the mic on). With `listen`, the processed sound also plays back (use headphones).
 */
function MicLevel({ deviceId, active, listen }: { deviceId: string; active: boolean; listen: boolean }) {
  const bar = useRef<HTMLSpanElement>(null);
  const { denoise, autoGain } = useDevices();
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let frame = 0;
    let stream: MediaStream | null = null;
    let processor: ReturnType<typeof createDenoiser> | null = null;
    const nodes: AudioNode[] = [];
    const ctx = denoiseContext();
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(deviceId === 'default' ? {} : { deviceId: { exact: deviceId } }),
            echoCancellation: true,
            noiseSuppression: denoise === 'browser',
            autoGainControl: autoGain,
          },
        });
        if (stopped) return stream.getTracks().forEach((t) => t.stop());
        await ctx.resume();
        let track = stream.getAudioTracks()[0]!;
        if (denoise === 'strong' || denoise === 'experimental') {
          processor = createDenoiser(denoise === 'strong' ? 'rnnoise' : 'gtcrn');
          await processor.init({ kind: 'audio' as never, track, audioContext: ctx });
          track = processor.processedTrack ?? track;
        }
        if (stopped) return;
        const source = ctx.createMediaStreamSource(new MediaStream([track]));
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        nodes.push(source, analyser);
        if (listen) source.connect(ctx.destination);
        const data = new Uint8Array(analyser.fftSize);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (const v of data) sum += ((v - 128) / 128) ** 2;
          const level = Math.min(1, Math.sqrt(sum / data.length) * 4);
          if (bar.current) bar.current.style.width = `${Math.round(level * 100)}%`;
          frame = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        // permission denied or device gone: the bar simply stays empty
      }
    })();
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      for (const node of nodes) node.disconnect();
      void processor?.destroy();
      stream?.getTracks().forEach((t) => t.stop());
      if (bar.current) bar.current.style.width = '0%';
    };
  }, [deviceId, active, listen, denoise, autoGain]);

  return (
    <div className="h-2 overflow-hidden rounded-full bg-surface-3" role="img" aria-label="Mikrofon seviyesi">
      <span ref={bar} className="block h-full w-0 rounded-full bg-green-400 transition-[width] duration-75" />
    </div>
  );
}

const platformIcon = { web: Monitor, desktop: Laptop, ios: Smartphone, android: Smartphone } as const;
const ago = new Intl.RelativeTimeFormat('tr', { numeric: 'auto' });

function relative(iso: string): string {
  const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000);
  if (Math.abs(minutes) < 60) return ago.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return ago.format(hours, 'hour');
  return ago.format(Math.round(hours / 24), 'day');
}

function Sessions() {
  const queries = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => client.api.sessions(), staleTime: 0 });
  const [error, setError] = useState<string | null>(null);

  async function end(session: SessionInfo) {
    setError(null);
    try {
      await client.api.endSession(session.id);
      await queries.invalidateQueries({ queryKey: ['sessions'] });
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  if (sessions.isPending) return <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>;
  // A failed refresh keeps the list we already have; the error only replaces it when there is none.
  if (!sessions.data) return <p className="m-0 text-sm text-[#EE9191]">{errorMessage(sessions.error)}</p>;
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-[13px] leading-normal text-fg-3">NovaChat’in açık olduğu cihazlar. Tanımadığın ya da artık kullanmadığın bir cihazdan çıkış yaptırabilirsin.</p>
      {error && <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">{error}</p>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {sessions.data.map((session) => {
          const Icon = platformIcon[session.platform];
          return (
            <li key={session.id} className="flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5">
              <Icon size={18} className="flex-none text-fg-2" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{session.deviceName}</div>
                <div className="text-xs text-fg-3">Son kullanım {relative(session.lastUsedAt)}</div>
              </div>
              {session.current ? (
                <span className="rounded-xs bg-green-400/15 px-2 py-1 text-xs font-semibold text-green-400">Bu cihaz</span>
              ) : (
                <button type="button" onClick={() => void end(session)} className="nc-press h-8 rounded-sm border border-line-strong bg-surface-1 px-2.5 text-[13px] font-semibold text-[#EE9191]">
                  Çıkış yaptır
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
